import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TopBar } from "@/components/PageShell";
import {
  BookMarked, Sparkles, FileText, Trash2, Copy, Search, Loader2, Inbox,
  Wand2, ClipboardList, X, Play, ArrowRight, Users, Landmark, Hash, AlertCircle,
} from "lucide-react";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const fmtDT = (s) => s
  ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
  : "—";

export default function ModelosConverge() {
  const [params] = useSearchParams();
  const highlightId = params.get("highlight");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(highlightId || null);
  const [applyId, setApplyId] = useState(null);

  useEffect(() => {
    let stop = false;
    api.get("/converge-templates").then((r) => {
      if (stop) return;
      setItems(r.data.items || []);
    }).catch(() => toast.error("Falha ao carregar templates"))
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter(i =>
      (i.title + " " + i.document_type + " " + (i.notes || "")).toLowerCase().includes(t)
    );
  }, [items, q]);

  const openDoc = items.find(i => i.id === openId) || null;

  const remove = async (tid) => {
    if (!window.confirm("Excluir este template?")) return;
    try {
      await api.delete(`/converge-templates/${tid}`);
      setItems((prev) => prev.filter(i => i.id !== tid));
      if (openId === tid) setOpenId(null);
      toast.success("Template excluído");
    } catch { toast.error("Falha ao excluir"); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="modelos-converge-page">
      <TopBar/>
      <div className="p-3 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              <BookMarked className="w-3 h-3 inline mr-1"/> Modelos & Cláusulas · Convergência
            </div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: NAVY }}>
              Templates de convergência
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Templates gerados por IA a partir da comparação de duas peças similares. Blocos idênticos preservados verbatim, divergências viram placeholders.
            </p>
          </div>
          <Link to="/preparados" data-testid="modelos-converge-cta"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95"
            style={{ background: NAVY }}>
            <Sparkles className="w-4 h-4"/> Comparar novas peças
          </Link>
        </div>

        <div className="relative mb-4 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título ou tipo…"
            data-testid="modelos-converge-search"
            className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#c9953d]/40 focus:border-[#c9953d]"/>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
        ) : filtered.length === 0 ? (
          <EmptyState hasItems={items.length > 0}/>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((t) => (
              <TemplateCard key={t.id} tpl={t}
                highlighted={t.id === highlightId}
                onOpen={() => setOpenId(t.id)}
                onApply={() => setApplyId(t.id)}
                onDelete={() => remove(t.id)}/>
            ))}
          </div>
        )}
      </div>

      {openDoc && <TemplateDrawer tpl={openDoc}
                    onClose={() => setOpenId(null)}
                    onApply={() => { setApplyId(openDoc.id); }}
                    onDelete={remove}/>}
      {applyId && <ApplyTemplateModal
                    tpl={items.find(i => i.id === applyId)}
                    onClose={() => setApplyId(null)}/>}
    </div>
  );
}

function TemplateCard({ tpl, highlighted, onOpen, onApply, onDelete }) {
  const varsUsed = (tpl.variables || []).length;
  return (
    <div data-testid={`converge-tpl-${tpl.id}`}
      className={`rounded-xl border p-4 transition ${
        highlighted ? "border-amber-300 ring-2 ring-amber-200 shadow-lg bg-amber-50/40" :
        "border-slate-200 bg-white hover:shadow-md hover:border-slate-300"
      }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className="text-[9px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded"
                  style={{ background: NAVY }}>
              <Wand2 className="w-2.5 h-2.5 inline mr-0.5"/> IA CONVERGÊNCIA
            </span>
            <span className="text-[10px] text-slate-500 uppercase tracking-widest">
              {tpl.document_type}
            </span>
          </div>
          <div className="font-semibold text-slate-900 mt-1" style={{ color: NAVY }}>
            <FileText className="w-4 h-4 inline mr-1 text-slate-400"/>
            {tpl.title}
          </div>
          <div className="text-[11.5px] text-slate-500 mt-1 flex gap-3 flex-wrap">
            {tpl.source?.coverage_pct != null && (
              <span>Cobertura: <b>{tpl.source.coverage_pct}%</b></span>
            )}
            <span>{varsUsed} variáve{varsUsed !== 1 ? "is" : "l"}</span>
            <span>Criado {fmtDT(tpl.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
        <button onClick={() => onDelete(tpl.id)}
          data-testid={`converge-tpl-del-${tpl.id}`}
          className="h-8 px-2.5 rounded border border-rose-200 text-rose-700 text-[11.5px] hover:bg-rose-50 inline-flex items-center gap-1">
          <Trash2 className="w-3 h-3"/> Excluir
        </button>
        <button onClick={onOpen} data-testid={`converge-tpl-open-${tpl.id}`}
          className="h-8 px-3 rounded border border-slate-200 bg-white text-slate-700 text-[12px] font-semibold hover:border-slate-300 inline-flex items-center gap-1">
          Ver
        </button>
        <button onClick={onApply} data-testid={`converge-tpl-apply-${tpl.id}`}
          className="h-8 px-3 rounded text-white text-[12px] font-semibold hover:opacity-95 inline-flex items-center gap-1"
          style={{ background: GOLD }}>
          <Play className="w-3 h-3"/> Aplicar
        </button>
      </div>
    </div>
  );
}

function TemplateDrawer({ tpl, onClose, onApply, onDelete }) {
  const highlighted = useMemo(() => {
    const parts = (tpl.content_markdown || "").split(/(\{\{\s*[A-Z0-9_]+\s*\}\})/g);
    return parts.map((p, i) => {
      if (/^\{\{\s*[A-Z0-9_]+\s*\}\}$/.test(p)) {
        return (
          <span key={i} className="bg-amber-100 text-amber-900 px-1 rounded font-mono text-[11.5px] font-semibold border border-amber-300 mx-0.5">
            {p}
          </span>
        );
      }
      return <span key={i}>{p}</span>;
    });
  }, [tpl.content_markdown]);

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(tpl.content_markdown);
      toast.success("Markdown copiado");
    } catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch justify-end" onClick={onClose}
         data-testid="tpl-drawer">
      <div className="w-full max-w-3xl bg-white h-full overflow-hidden flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <BookMarked className="w-5 h-5" style={{ color: GOLD }}/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{tpl.document_type}</div>
            <h2 className="font-heading text-lg font-semibold truncate" style={{ color: NAVY }}>
              {tpl.title}
            </h2>
          </div>
          <button onClick={copyMarkdown}
            className="h-8 px-2.5 rounded border border-slate-200 text-slate-600 text-[11.5px] hover:border-slate-300 inline-flex items-center gap-1">
            <Copy className="w-3 h-3"/> Copiar
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {(tpl.variables || []).length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-2">Variáveis</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {tpl.variables.map((v) => (
                  <div key={v.name} className="rounded-lg border border-amber-200 bg-amber-50/40 p-2">
                    <code className="font-mono text-[11.5px] font-bold text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                      {`{{${v.name}}}`}
                    </code>
                    <div className="text-[12px] text-slate-700 mt-1">{v.description}</div>
                    {v.examples?.length > 0 && (
                      <div className="text-[11px] text-slate-500 mt-1 truncate">
                        Ex.: {v.examples[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-2">Template</div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono text-slate-800"
                 data-testid="tpl-drawer-body">
              {highlighted}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
          <button onClick={() => onDelete(tpl.id)}
            className="h-9 px-3 rounded border border-rose-200 text-rose-700 text-[12.5px] hover:bg-rose-50 inline-flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5"/> Excluir template
          </button>
          <button onClick={onClose} className="ml-auto h-9 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm">
            Fechar
          </button>
          <button onClick={onApply} data-testid="tpl-drawer-apply"
            className="h-9 px-4 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:opacity-95"
            style={{ background: GOLD }}>
            <Play className="w-3.5 h-3.5"/> Aplicar template
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ hasItems }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="modelos-converge-empty">
      <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
      <div className="text-slate-800 font-semibold">
        {hasItems ? "Nenhum template com esse filtro" : "Nenhum template salvo ainda"}
      </div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {hasItems
          ? "Ajuste sua busca ou limpe o filtro."
          : "Compare duas peças similares em Preparados e gere um template reutilizável com IA."}
      </p>
      {!hasItems && (
        <Link to="/preparados" data-testid="modelos-converge-empty-cta"
          className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-95"
          style={{ background: NAVY }}>
          <Sparkles className="w-4 h-4"/> Ir aos preparados
        </Link>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Aplicar Template · formulário de placeholders → cria Preparado
// -----------------------------------------------------------------------------
function ApplyTemplateModal({ tpl, onClose }) {
  const navigate = useNavigate();

  // Extrai placeholders reais do template (fonte da verdade)
  const placeholders = useMemo(() => {
    const found = new Set();
    const re = /\{\{\s*([A-Z0-9_]+)\s*\}\}/g;
    let m;
    while ((m = re.exec(tpl?.content_markdown || "")) !== null) found.add(m[1]);
    return Array.from(found);
  }, [tpl]);

  // Metadados por variável (description + examples) vindos do template salvo
  const varMeta = useMemo(() => {
    const map = {};
    for (const v of (tpl?.variables || [])) if (v?.name) map[v.name] = v;
    return map;
  }, [tpl]);

  const [values, setValues] = useState(() => Object.fromEntries(placeholders.map(p => [p, ""])));
  const [title, setTitle] = useState(tpl?.title || "");
  const [processNumber, setProcessNumber] = useState("");
  const [tribunal, setTribunal] = useState("");
  const [orgao, setOrgao] = useState("");
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    // pré-carrega valores dos exemplos se disponíveis
    setValues((prev) => {
      const next = { ...prev };
      placeholders.forEach((p) => {
        if (!next[p]) next[p] = "";
      });
      // remove chaves que não existem mais
      Object.keys(next).forEach((k) => { if (!placeholders.includes(k)) delete next[k]; });
      return next;
    });
  }, [placeholders]);

  useEffect(() => {
    api.get("/clients", { params: { limit: 200 } })
      .then((r) => setClients(r.data?.items || r.data || []))
      .catch(() => {});
  }, []);

  const filled = useMemo(() => {
    let out = tpl?.content_markdown || "";
    placeholders.forEach((p) => {
      const val = (values[p] || "").trim();
      if (val) out = out.replace(new RegExp(`\\{\\{\\s*${p}\\s*\\}\\}`, "g"), val);
    });
    return out;
  }, [tpl, placeholders, values]);

  const highlightedFilled = useMemo(() => {
    // marca placeholders ainda vazios
    const parts = filled.split(/(\{\{\s*[A-Z0-9_]+\s*\}\})/g);
    return parts.map((p, i) => {
      if (/^\{\{\s*[A-Z0-9_]+\s*\}\}$/.test(p)) {
        return (
          <span key={i} className="bg-rose-100 text-rose-800 px-1 rounded font-mono text-[11.5px] font-semibold border border-rose-300 mx-0.5">
            {p}
          </span>
        );
      }
      return <span key={i}>{p}</span>;
    });
  }, [filled]);

  const missing = placeholders.filter(p => !(values[p] || "").trim());
  const canSubmit = missing.length === 0 && title.trim();

  const submit = async () => {
    if (!canSubmit) {
      if (missing.length) toast.error(`Preencha ${missing.length} variáve${missing.length !== 1 ? "is" : "l"}`);
      else if (!title.trim()) toast.error("Título obrigatório");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/converge-templates/${tpl.id}/apply`, {
        values,
        title: title.trim(),
        process_number: processNumber || null,
        tribunal: tribunal || null,
        orgao: orgao || null,
        client_id: clientId || null,
        strict: true,
      });
      toast.success("Peça criada em Preparados");
      onClose();
      navigate(`/preparado/${r.data.prepared_document_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Falha ao aplicar template");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}
         data-testid="apply-template-modal">
      <div className="w-full max-w-4xl bg-white h-full overflow-hidden flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GOLD }}>
            <Play className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              Aplicar template
            </div>
            <h2 className="font-heading text-xl font-semibold truncate" style={{ color: NAVY }}>
              {tpl?.title}
            </h2>
          </div>
          <button onClick={onClose} data-testid="apply-close"
                  className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {/* Progress banner */}
        <div className="px-6 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-3 text-[12px]">
          <span className="text-slate-500 uppercase tracking-widest text-[10px]">Progresso</span>
          <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full transition-all ${missing.length === 0 ? "bg-emerald-500" : "bg-amber-500"}`}
                 style={{ width: placeholders.length ? `${((placeholders.length - missing.length) / placeholders.length) * 100}%` : "100%" }}/>
          </div>
          <span className={`font-mono text-[11px] ${missing.length === 0 ? "text-emerald-700" : "text-amber-700"}`}
                data-testid="apply-progress">
            {placeholders.length - missing.length}/{placeholders.length}
          </span>
          <button onClick={() => setPreviewMode((v) => !v)} data-testid="apply-toggle-preview"
            className={`h-7 px-2.5 rounded border text-[11px] font-semibold ${
              previewMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}>
            {previewMode ? "Voltar ao formulário" : "Ver preview em tela cheia"}
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {!previewMode ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full">
              {/* Coluna esquerda: formulário */}
              <div className="p-5 space-y-4 overflow-y-auto border-r border-slate-100">
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Título da peça</label>
                  <input value={title} maxLength={200}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="apply-title"
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"/>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10.5px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                      <Hash className="w-3 h-3"/> Nº do processo (opcional)
                    </label>
                    <input value={processNumber} onChange={(e) => setProcessNumber(e.target.value)}
                      data-testid="apply-process"
                      className="mt-1 w-full h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 font-mono"/>
                  </div>
                  <div>
                    <label className="text-[10.5px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                      <Landmark className="w-3 h-3"/> Tribunal (opcional)
                    </label>
                    <input value={tribunal} onChange={(e) => setTribunal(e.target.value)}
                      data-testid="apply-tribunal"
                      placeholder="Ex.: TRF6"
                      className="mt-1 w-full h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"/>
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Órgão (opcional)</label>
                    <input value={orgao} onChange={(e) => setOrgao(e.target.value)}
                      data-testid="apply-orgao"
                      placeholder="Ex.: 01ª Vara Cível de Uberlândia"
                      className="mt-1 w-full h-9 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"/>
                  </div>
                  {clients.length > 0 && (
                    <div className="col-span-2">
                      <label className="text-[10.5px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
                        <Users className="w-3 h-3"/> Vincular cliente (opcional)
                      </label>
                      <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                        data-testid="apply-client"
                        className="mt-1 w-full h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                        <option value="">— Nenhum —</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nome_completo || c.name || c.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Placeholders */}
                <div>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                    <BookMarked className="w-3 h-3"/>
                    Variáveis do template
                    {missing.length > 0 && (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        <AlertCircle className="w-2.5 h-2.5"/> {missing.length} faltando
                      </span>
                    )}
                  </div>
                  {placeholders.length === 0 ? (
                    <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50">
                      Este template não tem placeholders — a peça será gerada exatamente como está.
                    </div>
                  ) : (
                    <div className="space-y-2.5" data-testid="apply-vars">
                      {placeholders.map((name) => {
                        const meta = varMeta[name] || {};
                        const empty = !(values[name] || "").trim();
                        return (
                          <div key={name} data-testid={`apply-var-${name}`}
                            className={`rounded-lg border p-2.5 ${empty ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/40"}`}>
                            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                              <code className={`font-mono text-[11.5px] font-bold rounded px-1.5 py-0.5 border ${
                                empty ? "text-amber-900 bg-amber-100 border-amber-300"
                                      : "text-emerald-900 bg-emerald-100 border-emerald-300"
                              }`}>
                                {`{{${name}}}`}
                              </code>
                              {meta.description && (
                                <span className="text-[11px] text-slate-600 text-right flex-1 truncate">{meta.description}</span>
                              )}
                            </div>
                            <input value={values[name] || ""}
                              onChange={(e) => setValues((v) => ({ ...v, [name]: e.target.value }))}
                              placeholder={meta.examples?.[0] ? `Ex.: ${meta.examples[0]}` : `Valor para ${name}`}
                              data-testid={`apply-var-input-${name}`}
                              className="w-full h-9 px-2.5 border border-slate-200 rounded text-[13px] bg-white focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"/>
                            {meta.examples?.length > 1 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {meta.examples.slice(0, 3).map((ex, i) => (
                                  <button key={i} type="button"
                                    onClick={() => setValues((v) => ({ ...v, [name]: String(ex) }))}
                                    className="text-[10.5px] px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-600 hover:border-amber-300 hover:text-amber-800 font-mono">
                                    {String(ex).slice(0, 30)}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Coluna direita: preview live */}
              <div className="bg-slate-50 flex flex-col overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-100 bg-white flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-slate-500"/>
                  <span className="text-[10.5px] uppercase tracking-widest text-slate-500">Preview em tempo real</span>
                  {missing.length === 0 && (
                    <span className="ml-auto text-[10.5px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                      Completo
                    </span>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono text-slate-800"
                     data-testid="apply-preview">
                  {highlightedFilled}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-5 h-full">
              <div className="rounded-xl border border-slate-200 bg-white p-5 h-full overflow-y-auto text-[13px] leading-relaxed whitespace-pre-wrap font-mono text-slate-800"
                   data-testid="apply-preview-full">
                {highlightedFilled}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
          <div className="text-[11px] text-slate-500 mr-auto">
            {missing.length === 0
              ? "Tudo preenchido — a peça vai direto para Preparados como AGUARDANDO_REVISAO."
              : `Preencha ${missing.length} variáve${missing.length !== 1 ? "is" : "l"} para continuar.`}
          </div>
          <button onClick={onClose} disabled={saving}
            className="h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm hover:border-slate-300">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving || !canSubmit}
            data-testid="apply-submit"
            className="h-10 px-5 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-95 disabled:opacity-50"
            style={{ background: canSubmit ? GOLD : "#94a3b8" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4"/>}
            Gerar peça em Preparados
          </button>
        </div>
      </div>
    </div>
  );
}

